import type { CSSProperties } from 'react';
import { useState } from 'react';

import type { RegionNode } from '../../../../api/regions';

const rowStyle = (depth: number, selected: boolean): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  textAlign: 'left',
  padding: '7px 10px',
  paddingLeft: 10 + depth * 18,
  borderRadius: 8,
  border: 'none',
  cursor: 'pointer',
  background: selected ? 'var(--nb-primary-soft)' : 'transparent',
  font: 'inherit',
});

// Root-region color palette (carried over from the legacy settings tree). Used
// as the picker options and as the fallback when a root has no stored color.
export const ROOT_COLORS = [
  '#059669',
  '#2563eb',
  '#dc2626',
  '#9333ea',
  '#ea580c',
  '#0891b2',
  '#ca8a04',
  '#be185d',
  '#4f46e5',
  '#16a34a',
];

/**
 * Map each region id → the hex color inherited from its root ancestor. A root's
 * own `color` (if set) wins; otherwise it falls back to the palette by index.
 */
export function buildColorMap(regions: RegionNode[]): Map<string, string> {
  const map = new Map<string, string>();
  const walk = (node: RegionNode, color: string) => {
    map.set(node.id, color);
    node.children.forEach((c) => walk(c, color));
  };
  regions.forEach((r, i) => walk(r, r.color ?? ROOT_COLORS[i % ROOT_COLORS.length]));
  return map;
}

interface RowProps {
  node: RegionNode;
  depth: number;
  colorMap: Map<string, string>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** When set, rows are drag-to-reparent: drag a region onto another to move it. */
  onReparent?: (dragId: string, dropId: string) => void;
}

/** Initials + first name pill for a region's assigned regional director. */
function DirectorPill({ name, color }: { name: string; color: string }) {
  const parts = name.trim().split(/\s+/);
  const initials = (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
  return (
    <span
      title={`Regional director: ${name}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: 20,
        padding: '0 8px 0 3px',
        borderRadius: 999,
        border: '1px solid var(--nb-border)',
        background: 'var(--nb-surface)',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 16,
          height: 16,
          borderRadius: 999,
          background: color,
          color: '#fff',
          fontSize: 8.5,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {initials}
      </span>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--nb-ink)' }}>{parts[0]}</span>
    </span>
  );
}

function RegionRow({ node, depth, colorMap, selectedId, onSelect, onReparent }: RowProps) {
  const [expanded, setExpanded] = useState(depth === 0);
  const hasChildren = node.children.length > 0;
  const color = colorMap.get(node.id) ?? 'var(--nb-primary)';
  const selected = selectedId === node.id;

  return (
    <div>
      <button
        type="button"
        onClick={() => onSelect(node.id)}
        draggable={Boolean(onReparent)}
        onDragStart={(e) => e.dataTransfer.setData('text/plain', node.id)}
        onDragOver={(e) => onReparent && e.preventDefault()}
        onDrop={(e) => {
          if (!onReparent) return;
          e.preventDefault();
          const dragId = e.dataTransfer.getData('text/plain');
          if (dragId && dragId !== node.id) onReparent(dragId, node.id);
        }}
        className="nb-region-row"
        style={rowStyle(depth, selected)}
      >
        {hasChildren ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            style={{ width: 14, color: 'var(--nb-subtle)', flexShrink: 0 }}
          >
            {expanded ? '▾' : '▸'}
          </span>
        ) : (
          <span style={{ width: 14, flexShrink: 0 }} />
        )}
        <span style={{ width: 9, height: 9, borderRadius: 3, background: color, flexShrink: 0 }} />
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--nb-ink)' }}>
          {node.name}
        </span>
        {node.directorName && <DirectorPill name={node.directorName} color={color} />}
        <span className="nb-mono" style={{ fontSize: 11, color: 'var(--nb-subtle)' }}>
          {node.venueCount}
        </span>
      </button>
      {hasChildren && expanded && (
        <div>
          {node.children.map((child) => (
            <RegionRow
              key={child.id}
              node={child}
              depth={depth + 1}
              colorMap={colorMap}
              selectedId={selectedId}
              onSelect={onSelect}
              onReparent={onReparent}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface TreeProps {
  regions: RegionNode[];
  colorMap: Map<string, string>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onReparent?: (dragId: string, dropId: string) => void;
}

/** Region hierarchy tree with root-color inheritance + single selection. */
export function RegionHierarchyTree({
  regions,
  colorMap,
  selectedId,
  onSelect,
  onReparent,
}: TreeProps) {
  if (regions.length === 0) {
    return (
      <div style={{ padding: '16px 12px', fontSize: 13, color: 'var(--nb-subtle)' }}>
        No regions yet.
      </div>
    );
  }
  return (
    <div role="tree" aria-label="Region hierarchy">
      {regions.map((r) => (
        <RegionRow
          key={r.id}
          node={r}
          depth={0}
          colorMap={colorMap}
          selectedId={selectedId}
          onSelect={onSelect}
          onReparent={onReparent}
        />
      ))}
    </div>
  );
}
